// Axios 客户端 + JWT 拦截器(含 401 静默续期)
import axios, { AxiosError, type AxiosInstance, type AxiosResponse, type InternalAxiosRequestConfig } from 'axios';
import { useAuthStore, withPermissions } from '@/store/auth';
import type { ApiErrorBody, ApiErrorCode, RefreshResponse } from './types';

export const apiClient: AxiosInstance = axios.create({
  baseURL: '/api',
  timeout: 15_000,
  headers: { 'content-type': 'application/json' },
});

// 纯刷新客户端:不带任何拦截器,专供 token 续期与"无需鉴权"的请求使用,
// 避免 refresh 请求再走进 401 拦截造成无限递归。
export const rawClient: AxiosInstance = axios.create({
  baseURL: '/api',
  timeout: 15_000,
  headers: { 'content-type': 'application/json' },
});

// 请求拦截:加 Bearer token
apiClient.interceptors.request.use((config) => {
  const token = useAuthStore.getState().accessToken;
  if (token) {
    config.headers = config.headers ?? {};
    config.headers.authorization = `Bearer ${token}`;
  }
  return config;
});

// 响应拦截:把后端错误统一成 ApiError
export class ApiError extends Error {
  constructor(
    public status: number,
    public code: ApiErrorCode,
    message: string,
    public field?: string,
    public retry_after_secs?: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// ----- 401 静默续期 -----

/**
 * 这些端点是"登录前/续期本身"用,401 不应触发续期:
 * login / register / captcha 在未登录时访问,refresh 换到失效也要直接暴露结果。
 */
const NO_REFRESH_PREFIXES = ['/v1/auth/login', '/v1/auth/register', '/v1/auth/refresh', '/v1/auth/captcha'];

function isRefreshableUrl(url: string | undefined): boolean {
  if (!url) return false;
  return !NO_REFRESH_PREFIXES.some((p) => url.startsWith(p));
}

// -----------------------------------------------------------------------------
// 401 静默续期:并发 401 全部"挂起"等待一次共享续期,续期成功后统一重放,
// 续期进行中绝不 reject 到 UI,从而避免"先弹 401 提示、再点才正常"的竞态。
// -----------------------------------------------------------------------------

/** 续期进行中的入口;用同一 Promise 汇集",期间新到的 401 直接并入重放队列 */
let refreshGate: Promise<boolean> | null = null;

/** 续期期间排队待重放的原始请求配置(保持入队顺序) */
let pendingReplays: InternalAxiosRequestConfig[] = [];

/** 续期结束后按 config 对应到各自的重放结果,使每个请求 resolve/ reject 自己的结果;用 WeakMap 防 config 引用泄漏 */
const replayMap = new WeakMap<InternalAxiosRequestConfig, { resolve: (r: AxiosResponse) => void; reject: (e: unknown) => void }>();

/** 某个请求是否已在续期重放队列中(防止同一 config 重复入队);续期周期结束后重建以释放 */
let marked = new WeakSet<InternalAxiosRequestConfig>();

/** 续期成功后重放全部排队的请求,并把每个请求的结果回填给对应的等待者 */
function replayAll(): void {
  const batch = pendingReplays;
  pendingReplays = [];
  for (const cfg of batch) {
    const waiters = replayMap.get(cfg);
    // 用 rawClient 重放:绕过响应拦截器,杜绝"重放又 401→再续期→再重放"的潜在循环。
    // header 已在此手动更新为新 token,无需请求拦截器注入。
    const request = (async () => {
      const newToken = useAuthStore.getState().accessToken;
      if (newToken) {
        cfg.headers = cfg.headers ?? {};
        cfg.headers.authorization = `Bearer ${newToken}`;
      }
      return await rawClient.request(cfg);
    })();
    request.then(
      (r) => waiters?.resolve(r),
      (e) => waiters?.reject(e),
    );
  }
}

/**
 * 排他地执行一次续期:把当前 token 换成新的。
 * 返回 true=续期成功(已更新 store);false=无 refresh token 或续期也失败(清空登录态)。
 */
async function doRefresh(): Promise<boolean> {
  const refreshToken = useAuthStore.getState().refreshToken;
  if (!refreshToken) return false;
  try {
    const { data } = await rawClient.post<RefreshResponse>(
      '/v1/auth/refresh',
      { refresh_token: refreshToken },
    );
    useAuthStore.setState({
      user: withPermissions(data.user),
      accessToken: data.access_token,
    });
    return true;
  } catch {
    // refresh 也失效 → 清空登录态,回到未登录;后续请求由 UI 守卫跳转登录
    useAuthStore.setState({ user: null, accessToken: null, refreshToken: null });
    return false;
  }
}

/**
 * 401 处理入口:进入续期重放流程。
 * 返回当前请求自身的重放结果 promise(其 resolve/reject 反映该请求续期后的真实结果)。
 */
function enterRefreshAndReplay(cfg: InternalAxiosRequestConfig): Promise<AxiosResponse> {
  // 为该请求建立自己的结果通道
  return new Promise<AxiosResponse>((resolve, reject) => {
    if (!marked.has(cfg)) {
      marked.add(cfg);
      pendingReplays.push(cfg);
      replayMap.set(cfg, { resolve, reject });
    }
    if (!refreshGate) {
      refreshGate = doRefresh()
        .then((ok) => {
          if (ok) replayAll();
          return ok;
        })
        .finally(() => {
          refreshGate = null;
          // 续期成功后重放结果已回填;清理排队标记
          const failed = pendingReplays;
          pendingReplays = [];
          marked = new WeakSet();
          // 未轮到重放的请求(本次周期已结束但仍排队)直接视为失败
          for (const cfg of failed) {
            const w = replayMap.get(cfg);
            w?.reject(new ApiError(401, 'unauthenticated', '登录已过期,请重新登录'));
            replayMap.delete(cfg);
          }
        });
    }
  });
}

apiClient.interceptors.response.use(
  (resp) => resp,
  async (err: AxiosError) => {
    // 已登录但 token 过期 → 挂起续期并重放,期间不 reject;续期失败则静默返回原始 401
    const cfg = err.config;
    if (err.response?.status === 401 && cfg && isRefreshableUrl(cfg.url)) {
      return enterRefreshAndReplay(cfg);
    }

    if (err.response) {
      const body = err.response.data as ApiErrorBody | undefined;
      const code = (body?.error?.code ?? 'internal') as ApiErrorCode;
      // message 可能已是通用 http statusText;优先取后端,后端没有才用默认英文
      const message = body?.error?.message ?? err.message;
      const field = body?.error?.field;
      const retryAfterSecs = body?.error?.retry_after_secs;
      return Promise.reject(
        new ApiError(err.response.status, code, message, field, retryAfterSecs),
      );
    }
    // 网络错误
    return Promise.reject(new ApiError(0, 'internal', err.message));
  },
);

/** 把后端错误码映射成 i18n 友好的中文消息(简单版,Phase 4 不做 i18n) */
export function explainError(err: unknown): string {
  if (err instanceof ApiError) {
    // 一些常见码做特别提示
    switch (err.code) {
      case 'user_already_exists':
        return '用户名或邮箱已被占用';
      case 'invalid_credentials':
        return '用户名/邮箱或密码错误';
      case 'unauthenticated':
        return '请先登录';
      case 'permission_denied':
        return '权限不足';
      case 'validation_failed':
        return err.field ? `${err.field}: ${err.message}` : `参数错误: ${err.message}`;
      case 'rate_limited': {
        const secs = err.retry_after_secs ?? 0;
        if (secs > 0) {
          return `操作太频繁,请 ${secs} 秒后再试`;
        }
        return '操作太频繁,请稍后再试';
      }
      default:
        return err.message;
    }
  }
  if (err instanceof Error) return err.message;
  return '未知错误';
}
